import { TeamGridOptionsType } from "@/types/ModuleOptionsType";
import { useGlobalContext } from "@aleksundshantu/aunds-utils-react/contexts";
import styles from "./TeamCard.module.scss";
import { ReactNode, useMemo } from "react";
import ModalTrigger from "../_primitives/Modal/ModalTrigger/ModalTrigger";
import { TeamCardElementType } from "./TeamCard";
import { Modul_Mitarbeiter_Grid } from "@/wp/types/acf-module-types";
import { Button } from "../Button/Button";

export const useTeamCard = (
  gridOptions: TeamGridOptionsType,
  team: TeamCardElementType,
  headlineTag: Modul_Mitarbeiter_Grid["team_grid"]["headline_html_tag"],
  skeleton = false,
) => {
  const {
    box: { show_border_radius, show_box_shadow, show_card_box },
    button_type,
    grid_options: { grid_type, team_item_view },
  } = gridOptions;

  const skeletonElementClass = skeleton ? "skeleton-element" : "";

  const showBox = show_card_box && grid_type === "text-below-image";
  const { getLabel } = useGlobalContext();
  const { element, url } = team;
  const teamMemberName = `${element.firstname} ${element.name}`;

  const cardClassName = useMemo(() => {
    const classes = [styles["team-card"]];

    if (showBox) {
      if (show_border_radius) classes.push("has-border-radius");
      if (show_box_shadow) classes.push("has-box-shadow");
    }

    return classes.join(" ");
  }, [showBox, show_border_radius, show_box_shadow]);

  const LinkJSX = useMemo(() => {
    const shouldApplyLinkClass = grid_type === "title-in-image";
    const isPopUp = team_item_view === "pop-up";

    // 1) If pop-up view, use modal trigger
    if (isPopUp) {
      const triggerBtn = (
        <Button
          className={`${shouldApplyLinkClass ? styles["team-card-link"] : ""} ${skeletonElementClass}`}
          href={url}
          target="_self"
          type={button_type}
          aria-label={`${getLabel("teamgrid_link_label")} von ${teamMemberName} öffnen`}
          disablePrefetch
        >
          {getLabel("teamgrid_link_label")}
        </Button>
      );
      return <ModalTrigger>{triggerBtn}</ModalTrigger>;
    }
    // 2) If not pop-up, but single view -> link to single page
    if (element.has_single_page) {
      return (
        <Button
          className={`${shouldApplyLinkClass ? styles["team-card-link"] : ""} ${skeletonElementClass}`}
          href={url}
          target="_self"
          type={button_type}
          aria-label={`${getLabel("teamgrid_link_label")} von ${teamMemberName} öffnen`}
          disablePrefetch
        >
          {getLabel("teamgrid_link_label")}
        </Button>
      );
    }
    return null;
  }, [
    grid_type,
    getLabel,
    url,
    button_type,
    team_item_view,
    teamMemberName,
    skeletonElementClass,
    element.has_single_page,
  ]);

  const NameJSX = useMemo(
    () => (
      <span
        role="heading"
        aria-level={headlineTag === "h3" ? 4 : 3}
        className={`card-title ${skeletonElementClass}`}
      >
        {teamMemberName}
      </span>
    ),
    [teamMemberName, headlineTag, skeletonElementClass],
  );

  const link = {
    url,
    aria_label: `${getLabel("teamgrid_link_label")} von ${teamMemberName} öffnen`,
    target: "_self",
    title: getLabel("teamgrid_link_label"),
  };

  const wrapInButtonForPopUp = (children: ReactNode) => {
    if (team_item_view !== "pop-up") return children;

    return (
      <ModalTrigger>
        <button
          className={styles["modal-trigger-button"]}
          aria-label={`${getLabel("teamgrid_link_label")} von ${teamMemberName} öffnen`}
        >
          {children}
        </button>
      </ModalTrigger>
    );
  };

  return { cardClassName, LinkJSX, NameJSX, link, wrapInButtonForPopUp };
};
